import React, { useState, useRef } from 'react';
import { Form, Input, Button, Alert, Checkbox, Grid, Modal } from 'antd';
import {
  MailOutlined, LockOutlined, UserOutlined, BankOutlined,
  KeyOutlined, SafetyCertificateOutlined, RocketOutlined,
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import axios from 'axios';

const { useBreakpoint } = Grid;

/* ── Right hero panel (gradient, dark) — mirrors Login.js HeroPanel ── */
const HeroPanel = () => (
  <div style={{
    flex: 1,
    background: 'linear-gradient(150deg, var(--brand-ink) 0%, oklch(0.32 0.12 265) 55%, oklch(0.28 0.15 263) 100%)',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '60px 56px', color: '#fff',
  }}>
    {/* Logo mark */}
    <div style={{
      width: 48, height: 48, borderRadius: 13,
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 32,
    }}>
      <img src="/icon.png" alt="" style={{ width: 30, height: 30, borderRadius: 7, display: 'block' }} />
    </div>

    <div style={{
      fontSize: 32, fontWeight: 750, letterSpacing: '-0.04em',
      lineHeight: 1.15, marginBottom: 16, maxWidth: 340,
    }}>
      Start sending smarter<br />email campaigns
    </div>

    <div style={{
      fontSize: 15, color: 'rgba(255,255,255,0.6)',
      marginBottom: 48, maxWidth: 360, lineHeight: 1.6,
    }}>
      Professional email marketing built on AWS SES. Full deliverability control, your own sending infrastructure.
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {[
        { icon: <KeyOutlined />,           text: 'Own your sending infrastructure with AWS SES' },
        { icon: <SafetyCertificateOutlined />,   text: 'Complete data isolation — your workspace, your data' },
        { icon: <RocketOutlined />,        text: 'Free to start, upgrade to Pro for unlimited sends' },
        { icon: <MailOutlined />,          text: 'Real-time open & click analytics on every campaign' },
      ].map(({ icon, text }) => (
        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: 'rgba(255,255,255,0.8)', flexShrink: 0,
          }}>
            {icon}
          </div>
          <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>{text}</span>
        </div>
      ))}
    </div>

    <div style={{
      marginTop: 56, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.1)',
      fontSize: 12.5, color: 'rgba(255,255,255,0.35)',
    }}>
      © {new Date().getFullYear()} SES Mailbox · The dashboard for AWS SES
    </div>
  </div>
);

/* hCaptcha site key */
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://sesmailbox.com';

/* ── Terms of Service Modal ── */
const TermsModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={<Button type="primary" onClick={onClose}>I Understand</Button>}
    title="Terms of Service"
    width={660}
    styles={{ body: { maxHeight: '60vh', overflowY: 'auto', padding: '4px 8px' } }}
  >
    <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.75 }}>
      <p style={{ color: '#6b7280', marginBottom: 16 }}>
        Last updated: {new Date().getFullYear()}. By creating an account you agree to these terms.
      </p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>1. Service Description</h4>
      <p>SES Mailbox ("the Service") provides email marketing infrastructure powered by Amazon Simple Email Service (SES). We allow you to send bulk emails to your own subscribers using your AWS SES credentials.</p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>2. Acceptable Use</h4>
      <p>You agree to use the Service only for lawful purposes. You must:</p>
      <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
        <li>Only send emails to recipients who have explicitly opted in to receive communications from you.</li>
        <li>Honour unsubscribe requests promptly (within 10 business days).</li>
        <li>Maintain a complaint rate below 0.1% and a bounce rate below 5%.</li>
        <li>Comply with all applicable laws including CAN-SPAM, CASL, and GDPR.</li>
      </ul>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>3. Prohibited Activities</h4>
      <p>You must NOT use the Service to:</p>
      <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
        <li>Send unsolicited commercial email (spam).</li>
        <li>Send phishing, malware, or deceptive content.</li>
        <li>Harvest or purchase email lists.</li>
        <li>Impersonate another person or organisation.</li>
        <li>Violate Amazon SES Acceptable Use Policy.</li>
      </ul>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>4. Account Responsibility</h4>
      <p>You are solely responsible for all activity under your account including the content of emails sent, the management of your subscriber lists, and the security of your AWS credentials stored within the Service.</p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>5. Suspension & Termination</h4>
      <p>We reserve the right to suspend or terminate accounts that violate these Terms, generate excessive complaints, or engage in abusive behaviour — without prior notice.</p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>6. Limitation of Liability</h4>
      <p>The Service is provided "as is" without warranty of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the Service.</p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>7. Changes to Terms</h4>
      <p>We may update these Terms at any time. Continued use of the Service after changes are posted constitutes acceptance of the new Terms.</p>
    </div>
  </Modal>
);

/* ── No-Spam Policy Modal ── */
const NoSpamModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={<Button type="primary" onClick={onClose}>I Commit to This</Button>}
    title="Anti-Spam & Email Sending Policy"
    width={660}
    styles={{ body: { maxHeight: '60vh', overflowY: 'auto', padding: '4px 8px' } }}
  >
    <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.75 }}>
      <p style={{ fontWeight: 600, color: '#1e1b4b', marginBottom: 12 }}>
        SES Mailbox has a zero-tolerance policy for spam. Violations will result in immediate account suspension.
      </p>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>✅ You must only send emails to people who have:</h4>
      <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
        <li>Explicitly opted in to receive emails from you (double opt-in preferred).</li>
        <li>A clear expectation of receiving communications from your organisation.</li>
        <li>Not previously unsubscribed or marked your emails as spam.</li>
      </ul>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>🚫 You must NEVER:</h4>
      <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
        <li>Purchase, rent, or scrape email lists.</li>
        <li>Add people to lists without their consent.</li>
        <li>Send emails after an unsubscribe request has been made.</li>
        <li>Use misleading subject lines, sender names, or "From" addresses.</li>
        <li>Send to role addresses (info@, admin@, support@) without express permission.</li>
      </ul>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>📋 Legal compliance</h4>
      <p>You are responsible for complying with all applicable email marketing laws in your jurisdiction, including:</p>
      <ul style={{ paddingLeft: 20, margin: '6px 0' }}>
        <li><strong>CAN-SPAM Act</strong> (USA) — identify commercial emails, include physical address, honour opt-outs within 10 days.</li>
        <li><strong>CASL</strong> (Canada) — obtain express or implied consent before sending.</li>
        <li><strong>GDPR</strong> (EU/UK) — require explicit consent, provide right to erasure.</li>
      </ul>

      <h4 style={{ color: '#111827', margin: '16px 0 6px' }}>⚠️ Enforcement</h4>
      <p>Accounts that generate spam complaints above 0.1%, bounce rates above 5%, or are found to be sending unsolicited mail will be <strong>suspended immediately</strong>. Repeated violations will result in permanent termination and may be reported to AWS SES Trust & Safety.</p>
    </div>
  </Modal>
);

/* ── Main ── */
const Register = () => {
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [tosOpen,      setTosOpen]      = useState(false);
  const [spamOpen,     setSpamOpen]     = useState(false);
  const [form]    = Form.useForm();
  const captchaRef = useRef(null);
  const navigate   = useNavigate();
  const screens    = useBreakpoint();
  const isMobile   = !screens.md;

  // Require captcha whenever the widget is showing the real (non-test) key
  const captchaRequired = HCAPTCHA_SITE_KEY !== '10000000-ffff-ffff-ffff-000000000001';

  const handleSubmit = async (values) => {
    if (captchaRequired && !captchaToken) {
      setCaptchaError(true);
      return;
    }
    setLoading(true);
    setError('');
    setCaptchaError(false);
    try {
      const res = await axios.post('/api/auth/register', {
        name:          values.name,
        email:         values.email,
        password:      values.password,
        captcha_token: captchaToken || '',
      });
      localStorage.setItem('token',     res.data.token);
      localStorage.setItem('userName',  res.data.name     || '');
      localStorage.setItem('userEmail', res.data.email    || '');
      localStorage.setItem('orgName',   res.data.org_name || '');
      // Track the signup conversion in GTM/GA4 (no PII — event + method only).
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'sign_up', method: 'email' });
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { borderRadius: 'var(--r-md)', height: 44 };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>

      {/* ── Left: form panel ── */}
      <div style={{
        width: isMobile ? '100%' : 500,
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '40px 24px' : '48px',
        background: 'var(--surface)',
        borderRight: isMobile ? 'none' : '1px solid var(--border)',
        overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          <a href={MARKETING_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 500, color: 'var(--text-3)', textDecoration: 'none', marginBottom: 22 }}>
            ← Back to home
          </a>

          {/* Logo */}
          <div style={{ marginBottom: 32 }}>
            <a href={MARKETING_URL} title="Back to sesmailbox.com" aria-label="Back to SES Mailbox home">
              <img src="/logo.png" alt="SES Mailbox" style={{ height: 56, width: 'auto', display: 'block' }} />
            </a>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 650, fontSize: 22, letterSpacing: '-0.03em', color: 'var(--text)', marginBottom: 4 }}>
              Create your account
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
              Start sending email campaigns with your own AWS SES
            </div>
          </div>

          {error && (
            <Alert
              message={error} type="error" showIcon closable onClose={() => setError('')}
              style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }}
            />
          )}

          <Form form={form} onFinish={handleSubmit} layout="vertical" size="large">
            <Form.Item
              name="name"
              label="Your Name"
              rules={[{ required: true, message: 'Enter your name' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: 'var(--text-3)' }} />}
                placeholder="Jane Smith"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email Address"
              rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'var(--text-3)' }} />}
                placeholder="jane@acme.com"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Enter a password' },
                { min: 8, message: 'Password must be at least 8 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--text-3)' }} />}
                placeholder="Min. 8 characters"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--text-3)' }} />}
                placeholder="Re-enter your password"
                style={inputStyle}
              />
            </Form.Item>

            {/* Terms of Service */}
            <Form.Item
              name="agree"
              valuePropName="checked"
              rules={[{
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('You must accept the Terms of Service to continue')),
              }]}
              style={{ marginBottom: 10 }}
            >
              <Checkbox style={{ color: 'var(--text-2)', fontSize: 13 }}>
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={e => { e.preventDefault(); setTosOpen(true); }}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--brand-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Terms of Service
                </button>
              </Checkbox>
            </Form.Item>

            {/* No-Spam Policy */}
            <Form.Item
              name="agree_nospam"
              valuePropName="checked"
              rules={[{
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('You must agree to the Anti-Spam Policy to continue')),
              }]}
              style={{ marginBottom: 20 }}
            >
              <Checkbox style={{ color: 'var(--text-2)', fontSize: 13 }}>
                I will only send emails to recipients who have{' '}
                <button
                  type="button"
                  onClick={e => { e.preventDefault(); setSpamOpen(true); }}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--brand-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  explicitly consented
                </button>
                {' '}— no purchased lists, no spam.
              </Checkbox>
            </Form.Item>

            {/* hCaptcha widget */}
            <Form.Item style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  theme="light"
                  onVerify={(token) => { setCaptchaToken(token); setCaptchaError(false); }}
                  onExpire={() => { setCaptchaToken(''); }}
                  onError={() => { setCaptchaToken(''); }}
                />
                {captchaError && (
                  <span style={{ color: 'var(--danger)', fontSize: 12 }}>
                    Please complete the CAPTCHA verification to continue.
                  </span>
                )}
              </div>
            </Form.Item>

            {/* Modals */}
            <TermsModal open={tosOpen} onClose={() => setTosOpen(false)} />
            <NoSpamModal open={spamOpen} onClose={() => setSpamOpen(false)} />

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{ height: 44, fontWeight: 600, borderRadius: 'var(--r-md)', fontSize: 14 }}
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-3)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* ── Right: hero panel (desktop only) ── */}
      {!isMobile && <HeroPanel />}
    </div>
  );
};

export default Register;
